import React, { ReactElement } from 'react';
import { ApplicationItem } from '../../services/applicationService';
import './ApplicationActionPanel.css';

interface ApplicationActionPanelProps {
  application: ApplicationItem;
  processingId: string | null;
  rejectionReason: string;
  onReasonChange: (reason: string) => void;
  onApprove: (application: ApplicationItem) => void;
  onReject: (application: ApplicationItem) => void;
  useStandardMessage: boolean;
  onUseStandardMessageChange: (useStandard: boolean) => void;
  standardMessages: string[];
  selectedStandardMessage: string;
  onStandardMessageSelect: (message: string) => void;
}

const ApplicationActionPanel: React.FC<ApplicationActionPanelProps> = ({
  application,
  processingId,
  rejectionReason,
  onReasonChange,
  onApprove,
  onReject,
  useStandardMessage,
  onUseStandardMessageChange,
  standardMessages,
  selectedStandardMessage,
  onStandardMessageSelect
}): ReactElement => {
  // Show approve button for pending and rejected status
  const showApproveButton = application.status === 'pending' || 
                            application.status === 'rejected';
  
  // Show reject form for pending and approved status
  const showRejectForm = application.status === 'pending' || 
                          application.status === 'approved';

  return (
    <div className="aap-container">
      {showApproveButton && (
        <button
          className="aap-button aap-approve"
          onClick={() => onApprove(application)}
          disabled={!!processingId}
        >
          {processingId === application.id ? 'Processing...' : 
            application.status === 'rejected' ? 'Change to Approved' : 'Approve Application'}
        </button>
      )}

      {showRejectForm && (
        <div className="aap-rejection-form">
          <div className="aap-standard-message-option">
            <label className="aap-checkbox-label">
              <div className="aap-checkbox-container">
                <input
                  type="checkbox"
                  checked={useStandardMessage}
                  onChange={(e) => onUseStandardMessageChange(e.target.checked)}
                  disabled={!!processingId}
                />
                <span>Use standardized rejection message</span>
              </div>
            </label>
          </div>
          
          {useStandardMessage ? (
            <select
              className="aap-standard-message-select"
              value={selectedStandardMessage}
              onChange={(e) => onStandardMessageSelect(e.target.value)}
              disabled={!!processingId}
              aria-label="Select standard rejection reason"
            >
              {standardMessages.length > 0 && 
                standardMessages.map((message, index) => (
                  <option key={index} value={message}>
                    {message}
                  </option>
                ))
              }
              {standardMessages.length === 0 && 
                <option value="">-- No standard messages available --</option>
              }
            </select>
          ) : (
            <>
              <textarea
                placeholder={application.status === 'approved' 
                  ? "Provide a reason for rejection (required)..." 
                  : "Provide a reason for rejection (optional)..."}
                value={rejectionReason}
                onChange={(e) => onReasonChange(e.target.value)}
                className={`aap-rejection-input ${application.status === 'approved' && !rejectionReason ? 'aap-required' : ''}`}
                disabled={!!processingId}
                required={application.status === 'approved'}
              ></textarea>
              {application.status === 'approved' && !rejectionReason && (
                <div className="aap-error-message">
                  * A rejection reason is required when rejecting a previously approved application
                </div>
              )}
            </>
          )}
          <button
            className="aap-button aap-reject"
            onClick={() => onReject(application)}
            disabled={!!processingId}
          >
            {processingId === application.id ? 'Processing...' : 
              application.status === 'approved' ? 'Change to Rejected' : 'Reject Application'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ApplicationActionPanel;
